// src/features/cart/pages/CartPage.tsx

import React, { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { cartService } from '../services/cartService';
import { CartItem } from '../types';
import { Toast } from '../../../components/common/Toast/Toast';
import { useCurrency } from '../../../contexts/CurrencyContext';

// "A1-01 - M, Gray"  →  baseName: "A1-01"
const parseBaseName = (name: string): string => {
  const dashIndex = name.lastIndexOf(' - ');
  return dashIndex === -1 ? name : name.slice(0, dashIndex).trim();
};

export const CartPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const currencySymbol = currency === 'THB' ? '฿' : '€';
  const { data, isLoading, error } = useQuery({
    queryKey: ['cart'],
    queryFn: cartService.getCart,
  });

  const items: CartItem[] = useMemo(() => data?.items ?? [], [data?.items]);

  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') =>
    setToast({ open: true, message, severity });

  const getQty = (item: CartItem) => quantities[item.cart_key] ?? item.quantity;

  const allChecked = items.length > 0 && items.every((i) => checkedKeys.has(i.cart_key));
  const someChecked = items.some((i) => checkedKeys.has(i.cart_key));

  const updateCartMutation = useMutation({
    mutationFn: cartService.updateCart,
    onSuccess: () => showToast('Updated quantity', 'success'),
    onError: () => showToast('Failed to update quantity', 'error'),
  });

  const removeCartMutation = useMutation({
    mutationFn: cartService.removeFromCart,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      showToast('Item removed', 'success');
    },
    onError: () => showToast('Failed to remove item', 'error'),
  });

  const handleToggleAll = () => {
    if (allChecked) {
      setCheckedKeys(new Set());
    } else {
      setCheckedKeys(new Set(items.map((i) => i.cart_key)));
    }
  };

  const handleToggleItem = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleQtyChange = (key: string, delta: number, current: number) => {
    const newQty = Math.max(1, current + delta);
    setQuantities((prev) => ({ ...prev, [key]: newQty }));
    updateCartMutation.mutate({ cart_key: key, quantity: newQty });
  };

  const handleDelete = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    removeCartMutation.mutate(key);
  };

  const selectedItems = useMemo(
    () => items.filter((i) => checkedKeys.has(i.cart_key)),
    [items, checkedKeys],
  );

  const totalItems = selectedItems.length;
  const totalPrice = selectedItems.reduce(
    (sum, i) => sum + (i.price?.[currency] ?? 0) * getQty(i),
    0,
  );

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'black' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Error loading cart: {(error as Error).message}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
      <Container maxWidth="md" sx={{ pt: { xs: 8, md: 10 }, pb: 12 }}>
        {/* Title */}
        <Typography
          sx={{
            fontSize: '32px',
            fontWeight: 400,
            textAlign: 'center',
            mb: 4,
            fontFamily: 'Nandia',
          }}
        >
          Shopping Bag
        </Typography>

        {/* Table wrapper */}
        <Box sx={{ border: '1px solid #D0D0D0', borderRadius: '4px 4px 0 0', overflow: 'hidden' }}>
          {/* Header row - desktop */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              gridTemplateColumns: '40px 1fr 80px 140px 120px 40px',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              bgcolor: '#F5F5F5',
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Checkbox
              checked={allChecked}
              indeterminate={!allChecked && someChecked}
              onChange={handleToggleAll}
              sx={{
                p: 0,
                color: '#333',
                '&.Mui-checked': { color: '#333' },
                '&.MuiCheckbox-indeterminate': { color: '#333' },
              }}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>All Order</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>Size</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>Quantity</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>Price</Typography>
            <Box />
          </Box>

          {/* Header row - mobile */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              alignItems: 'center',
              px: 2,
              py: 1.5,
              bgcolor: '#F5F5F5',
              borderBottom: '1px solid #E0E0E0',
              gap: 1,
            }}
          >
            <Checkbox
              checked={allChecked}
              indeterminate={!allChecked && someChecked}
              onChange={handleToggleAll}
              sx={{ p: 0, color: '#333', '&.Mui-checked': { color: '#333' }, '&.MuiCheckbox-indeterminate': { color: '#333' } }}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>All Order</Typography>
          </Box>

          {/* Empty state */}
          {items.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ color: '#999', fontSize: '16px' }}>
                Your shopping bag is empty.
              </Typography>
            </Box>
          )}

          {/* Item rows */}
          {items.map((item) => {
            const qty = getQty(item);
            const checked = checkedKeys.has(item.cart_key);
            const baseName = parseBaseName(item.name);
            const size = (item.attributes?.size ?? '-').toUpperCase();

            return (
              <Box key={item.cart_key}>
                {/* Desktop row */}
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gridTemplateColumns: '40px 1fr 80px 140px 120px 40px',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #F0F0F0',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggleItem(item.cart_key)}
                    sx={{ p: 0, color: '#333', '&.Mui-checked': { color: '#333' } }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box component="img" src={item.image} alt={baseName} sx={{ width: 64, height: 64, objectFit: 'cover', flexShrink: 0, borderRadius: '2px' }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>{baseName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ minWidth: 32, height: 32, px: 1, bgcolor: '#1A1A1A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, borderRadius: '2px' }}>
                      {size}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleQtyChange(item.cart_key, -1, qty)} disabled={qty <= 1} sx={{ width: 28, height: 28, color: '#333', '&:disabled': { color: '#ccc' } }}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 600, lineHeight: 1 }}>−</Typography>
                    </IconButton>
                    <Typography sx={{ width: 28, textAlign: 'center', fontSize: '14px', fontWeight: 600, userSelect: 'none' }}>{qty}</Typography>
                    <IconButton size="small" onClick={() => handleQtyChange(item.cart_key, 1, qty)} sx={{ width: 28, height: 28, color: '#333' }}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 600, lineHeight: 1 }}>+</Typography>
                    </IconButton>
                  </Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 400, textAlign: 'center' }}>
                    {currencySymbol} {((item.price?.[currency] ?? 0) * qty).toLocaleString()}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDelete(item.cart_key)} sx={{ color: '#666', '&:hover': { color: '#333' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Mobile row */}
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 1.5,
                    py: 1.5,
                    borderBottom: '1px solid #F0F0F0',
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={() => handleToggleItem(item.cart_key)}
                    sx={{ p: 0, mt: 0.5, color: '#333', '&.Mui-checked': { color: '#333' } }}
                  />
                  <Box component="img" src={item.image} alt={baseName} sx={{ width: 72, height: 72, objectFit: 'cover', flexShrink: 0, borderRadius: '2px' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 400, pr: 1 }}>{baseName}</Typography>
                      <IconButton size="small" onClick={() => handleDelete(item.cart_key)} sx={{ color: '#666', p: 0.25, flexShrink: 0 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 28, height: 28, px: 0.75, bgcolor: '#1A1A1A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, borderRadius: '2px', flexShrink: 0 }}>
                        {size}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => handleQtyChange(item.cart_key, -1, qty)} disabled={qty <= 1} sx={{ width: 24, height: 24, color: '#333', '&:disabled': { color: '#ccc' } }}>
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>−</Typography>
                        </IconButton>
                        <Typography sx={{ width: 24, textAlign: 'center', fontSize: '13px', fontWeight: 600, userSelect: 'none' }}>{qty}</Typography>
                        <IconButton size="small" onClick={() => handleQtyChange(item.cart_key, 1, qty)} sx={{ width: 24, height: 24, color: '#333' }}>
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>+</Typography>
                        </IconButton>
                      </Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, ml: 'auto' }}>
                        {currencySymbol} {((item.price?.[currency] ?? 0) * qty).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#1A1A1A',
            px: 3,
            py: 1.5,
            borderRadius: '0 0 4px 4px',
          }}
        >
          <Typography sx={{ color: '#fff', fontSize: '14px' }}>
            Total {totalItems} Items &nbsp;&nbsp; {currencySymbol} {totalPrice.toLocaleString()}
          </Typography>
          <Button
            variant="contained"
            disabled={totalItems === 0}
            onClick={() => {
              const itemsToCheckout = selectedItems.map((i) => ({
                ...i,
                quantity: quantities[i.cart_key] ?? i.quantity,
              }));
              navigate('/checkout', { state: { items: itemsToCheckout } });
            }}
            sx={{
              bgcolor: '#fff',
              color: '#1A1A1A',
              fontWeight: 600,
              fontSize: '14px',
              px: 4,
              py: 1,
              '&:hover': { bgcolor: '#f0f0f0' },
              '&:disabled': { bgcolor: '#555', color: '#888' },
            }}
          >
            Check Out
          </Button>
        </Box>
      </Container>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default CartPage;
